/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    cloneDeep as _cloneDeep,
    isEqual,
} from 'lodash';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import ConfirmationDialog from '../../../components/confirmationdlg';
import Validator, { useValidate, checkValidity } from '../../../components/validator';

import Auth from '../../../data/auth-service';
import ZeekService from '../../../data/zeek-service';
import SuricataService from '../../../data/suricata-service';

const ZeekSettings = () => {
    const navigate = useNavigate();
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [tiImports, setTIImports] = useState({
        frequency: 60,
        retries: 1,
        ti_urls: [],
        timeout: 120,
        urls: [],
    });
    const [original, setOriginal] = useState({});
    const [, setChanges] = useState(false);
    const zeekForm = useValidate({});
    const [, setManagedStatus] = useState(false);
    const zeekPerms = Auth.permissions.get('ZeekScripts');

    useEffect(() => {
        getURLConf();
        getManagedStatus();
    }, []);

    useEffect(() => {
        setChanges(isEqual(tiImports, original));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tiImports]);

    const getManagedStatus = () => {
        SuricataService.isManaged().then(res => {
            setManagedStatus(res.managed);
        }, err => {
            console.error('Unable to get managed status ::: ', err);
        });
    };

    const getURLConf = () => {
        ZeekService.getScriptsURL().then((res) => {
            if (!res.ti_urls || !res.ti_urls.length) {
                res.ti_urls = [{ url: '' }];
            }
            if (!res.urls || !res.urls.length) {
                res.urls = [{ url: '' }];
            }
            setOriginal(_cloneDeep(res));
            setTIImports(res);
        });
    };

    const handleUpdateImports = (e, validID, valid) => {
        zeekForm.set({ ...zeekForm.get(), [validID]: valid });

        setTIImports({
            ...tiImports,
            [e.target.name]: parseInt(e.target.value),
        });
    };

    const handleUpdateUrls = (e, i, validID, valid) => {
        zeekForm.set({ ...zeekForm.get(), [validID]: valid });

        const t = { ...tiImports };
        t[e.target.name][i].url = e.target.value;
        setTIImports(t);
    };

    const handleAddURL = (e) => {
        const t = { ...tiImports };
        t[e.currentTarget.name].push({ url: '' });
        setTIImports(t);
    };

    const handleRemoveURL = (e, i) => {
        const t = { ...tiImports };
        t[e.currentTarget.name].splice(i, 1);
        if (!t[e.currentTarget.name].length) {
            t[e.currentTarget.name].push({ url: '' });
        }
        setTIImports(t);
    };

    const handleSaveImports = (e, kick) => {
        const ti_urls = tiImports.ti_urls.filter(v => v.url.length > 0);
        const urls = tiImports.urls.filter(v => v.url.length > 0);
        ZeekService.updateScriptsURL({
            ...tiImports,
            ti_urls: ti_urls,
            urls: urls,
        }).then(res => {
            toast.success('Zeek settings saved successfully');
            if (kick) {
                kickIt();
            }
            getURLConf();
        }, err => {
            toast.error('Zeek settings update failed : ', err);
            if (kick) {
                toast.error('Zeek script manual import did not run due to update failure');
            }
        });
    };

    const handleRevertImports = () => {
        getURLConf();
    };

    const handleResetImports = () => {
        ZeekService.clearImportsDirectory().then(() => {
            toast.success('Import directory cleared successfully');
        });
        handleOpenDelete();
    };

    const handleOpenDelete = () => {
        setDeleteOpen(!deleteOpen);
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    const kickIt = () => {
        ZeekService.manuallyKickBroScriptsImport().then(res => {
            toast.success('Zeek script import started successfully. Please check audit logs for status.');
        }, err => {
            console.error(err);
        });
    };

    return (
        <main>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="flex-end">
                        {/* Don't underestimate the Force. */}
                        {/* <Box ml={2}>
                            {managedStatus ? <Tooltip title="Cannot force import when CMC is managed by MC2">
                                <span>
                                    <Button disabled variant="contained" color="primary" aria-label="manually start import">Force Import</Button>
                                </span>
                            </Tooltip> :
                                <Button variant="contained" color="primary" aria-label="manually start import" onClick={handleKickImports}>Force Import</Button>
                            }
                        </Box> */}
                        {zeekPerms.delete && <Box ml={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                aria-label="reset imports"
                                onClick={handleOpenDelete}
                            >Reset Imports
                            </Button>
                        </Box>}
                        {zeekPerms.write && <Box ml={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                aria-label="revert"
                                onClick={handleRevertImports}
                            >Revert
                            </Button>
                        </Box>}
                        {zeekPerms.write && <Box ml={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                aria-label="save"
                                onClick={handleSaveImports}
                                // disabled={!(checkValidity(zeekForm.get()) && !changes)}
                                disabled={!(checkValidity(zeekForm.get()))}
                            >
                                Save
                            </Button>
                        </Box>}
                        <Box ml={2}><Button variant="contained" aria-label="back" onClick={handleGoBack}>Back</Button></Box>
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <Paper elevation={3}>
                        <Box p={3}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="h5">Zeek Script Imports</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box>
                                        <Validator
                                            disabled={!zeekPerms.write}
                                            valueType="range"
                                            errorText="Frequency must be within 15 and 1440 minutes"
                                            inputProps={{
                                                min: 15,
                                                max: 1440,
                                            }}
                                            type="number"
                                            fullWidth
                                            label="Frequency"
                                            helperText="15m minimum"
                                            name="frequency"
                                            value={tiImports.frequency}
                                            onChange={handleUpdateImports}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box>
                                        <Validator
                                            disabled={!zeekPerms.write}
                                            valueType="range"
                                            errorText="Timeout must be within 11 and 120"
                                            inputProps={{
                                                min: 11,
                                                max: 120,
                                            }}
                                            type="number"
                                            fullWidth
                                            label="Timeout"
                                            helperText="11s minimum"
                                            name="timeout"
                                            value={tiImports.timeout}
                                            onChange={handleUpdateImports}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box>
                                        <Validator
                                            disabled={!zeekPerms.write}
                                            valueType="range"
                                            errorText="Retries must be within 1 and 20"
                                            inputProps={{
                                                min: 1,
                                                max: 20,
                                            }}
                                            type="number"
                                            fullWidth
                                            label="Retries"
                                            name="retries"
                                            value={tiImports.retries}
                                            onChange={handleUpdateImports}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item container spacing={2}>
                    <Grid item xs={9}>
                        <Paper elevation={3}>
                            <Box p={3}>
                                <Box mb={2} display="flex" justifyContent="space-between">
                                    <Typography>Zeek Script URLs</Typography>
                                    <IconButton
                                        disabled={!zeekPerms.write}
                                        size="small"
                                        aria-label="add url"
                                        name="urls"
                                        onClick={handleAddURL}><AddIcon /></IconButton>
                                </Box>
                                {tiImports.urls.map((v, i) => {
                                    return (
                                        <Box key={'zeek-import-url-' + i} mb={2}>
                                            <Validator
                                                valueType="url"
                                                errorText="URL is invalid"
                                                allowEmpty
                                                type="text"
                                                fullWidth
                                                placeholder="http://example.com/brolib.tar"
                                                name="urls"
                                                value={tiImports.urls[i].url}
                                                onChange={(e, validID, valid) => { handleUpdateUrls(e, i, validID, valid) }}
                                                endAdornment={
                                                    <InputAdornment position='end'>
                                                        <IconButton
                                                            disabled={!zeekPerms.delete}
                                                            aria-label="delete url"
                                                            name="urls"
                                                            onClick={(e) => { handleRemoveURL(e, i) }}
                                                            size="large">
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </InputAdornment>
                                                }
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Grid>
            <ConfirmationDialog
                onClose={handleOpenDelete}
                onOK={handleResetImports}
                open={deleteOpen}
                message={'Are you sure you want to clear the Zeek script import directory?'}
                title="Delete Imports"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
        </main>
    );
};

export default ZeekSettings;
